// VocalFitness phonetic engine: canonical articulations → Samuel-format trajectories.
import { PHONES, toSteps } from '../vocalTract/articulations';
import { FRAME_RATE } from './model';
import { transcribe } from '../vocalTract/lexicon';
import { VELUM_OPEN } from './engine';

const OPEN = 3;
const WEIGHT = { v: 1, vl: 1.5, d: 1.6, s: 0.8, a: 1, f: 0.9, n: 0.7, x: 0.6 };

// One articulatory state (ti/td/c/velum/voice/tenseness) → one Samuel frame.
export const stateToFrame = (s, prev = {}) => {
  const c = s.c || [];
  const lip = c.find(([i]) => i >= 39), tip = c.find(([i]) => i < 39);
  return {
    tongueIndex: s.ti ?? prev.tongueIndex ?? 20,
    tongueDiameter: s.td ?? prev.tongueDiameter ?? 2.9,
    constrictionIndex: tip ? tip[0] : prev.constrictionIndex ?? 33,
    constrictionDiameter: tip ? tip[1] + 0.3 : OPEN,
    lipDiameter: lip ? lip[1] + 0.3 : OPEN,
    velum: s.velum > 0.1 ? VELUM_OPEN : OPEN,
    voiceness: s.voice === false ? 0 : s.tenseness ?? 0.6,
    intensity: 1,
    frequency: s.frequency ?? prev.frequency ?? 120,
  };
};

const KEYS = ['tongueIndex', 'tongueDiameter', 'constrictionIndex', 'constrictionDiameter', 'lipDiameter', 'velum', 'voiceness', 'intensity', 'frequency'];
const lerp = (a, b, t) => a + (b - a) * t;
// 5-frame (~55 ms) moving average: removes the steps that click in the tract synth.
const smooth = (v, r = 2) => v.map((_, i) => {
  let s = 0, n = 0;
  for (let k = Math.max(0, i - r); k <= Math.min(v.length - 1, i + r); k++) { s += v[k]; n++; }
  return s / n;
});
// Median filter for pitch: drops single-frame octave jumps from the tracker.
const median = (v, r = 3) => v.map((_, i) => {
  const w = v.slice(Math.max(0, i - r), i + r + 1).sort((a, b) => a - b);
  return w[w.length >> 1];
});

// Timed steps [{state, dur(ms), label}] → response. f0 optional per-frame contour.
const stepsToResponse = (steps, { transitionMs = 55, lead = 0.08, tail = 0.12, f0 = null, total = null } = {}) => {
  const fr = FRAME_RATE, frames = [];
  const quiet = (f) => ({ ...f, intensity: 0, voiceness: f.voiceness });
  let prev = stateToFrame(steps[0]?.state || {});
  const push = (f) => frames.push(f);
  for (let i = 0; i < Math.round(lead * fr); i++) push(quiet(prev));
  const labels = [];
  steps.forEach((st) => {
    if (st.gap) { const n = Math.max(1, Math.round((st.dur / 1000) * fr)); for (let i = 0; i < n; i++) push(quiet(prev)); return; }
    const target = stateToFrame(st.state, prev);
    const n = Math.max(1, Math.round((st.dur / 1000) * fr)), tr = Math.max(1, Math.round((Math.min(transitionMs, st.dur) / 1000) * fr));
    labels.push({ sym: st.label, t0: frames.length / fr, t1: (frames.length + n) / fr });
    for (let i = 0; i < n; i++) {
      const t = Math.min(1, (i + 1) / tr), f = {};
      KEYS.forEach((k) => { f[k] = lerp(prev[k], target[k], t); });
      push(f);
    }
    prev = target;
  });
  const want = total ? Math.ceil(total * fr) : frames.length + Math.round(tail * fr);
  while (frames.length < want) push(quiet(prev));
  frames.length = Math.max(2, Math.min(frames.length, want));
  const params = Object.fromEntries(KEYS.map((k) => [k, smooth(frames.map((f) => f[k]))]));
  if (f0) params.frequency = smooth(median(params.frequency.map((v, i) => f0[Math.min(i, f0.length - 1)] || v)));
  else params.frequency = params.frequency.map((v, i) => v * (1.08 - (0.16 * i) / frames.length));
  return { frame_rate: fr, n_frames: frames.length, duration_s: frames.length / fr, params, voiced: params.voiceness.map((v) => v > 0.3), labels, source: 'vf' };
};

// Phones in sequence (no audio): canonical VocalFitness articulation.
export const phonesToResponse = (phones, { rate = 0.8, gaps = [], gapMs = 110 } = {}) => {
  const steps = [];
  phones.forEach((p, i) => {
    toSteps([p], { rate }).forEach((s) => steps.push(s));
    if (gaps.includes(i)) steps.push({ gap: true, dur: gapMs / rate });
  });
  return stepsToResponse(steps, { transitionMs: 55 / rate });
};

// IPA string (e.g. "ˈfɔə") → known phone symbols, longest match first.
const IPA_ALIAS = { 'ɹ': 'r', 'ɾ': 'd', g: 'ɡ', 'ɫ': 'l' };
export const ipaToPhones = (ipa) => {
  const s = [...String(ipa).normalize('NFC').replace(/[[\]/ˈˌ.\u0329\u0306]/g, '')].map((c) => IPA_ALIAS[c] || c).join('');
  const out = [];
  for (let i = 0; i < s.length;) {
    const hit = [3, 2, 1].map((n) => s.slice(i, i + n)).find((k) => PHONES[k]);
    if (hit) { out.push(hit); i += hit.length; } else i += 1;
  }
  return out;
};

// Distribute each word's phones inside its time span (from Whisper word timestamps).
export const alignPhones = (timedWords, g2pWords, dialect = 'GA') => {
  const out = [];
  const norm = (w) => String(w).toLowerCase().replace(/[^a-z']/g, '');
  let j = 0;
  timedWords.forEach((tw) => {
    let k = j;
    while (k < g2pWords.length && norm(g2pWords[k].word) !== norm(tw.word)) k++;
    const g = k < g2pWords.length ? g2pWords[k] : null;
    if (g) j = k + 1;
    const lex = transcribe(norm(tw.word), dialect === 'RP' ? 'RP' : 'AmE');
    const phones = (lex || g?.phones || []).filter((p) => PHONES[p]);
    if (!phones.length) return;
    const w = phones.map((p) => WEIGHT[PHONES[p].kind] || 1), sum = w.reduce((a, b) => a + b, 0);
    const span = Math.max(0.08, tw.end - tw.start);
    let t = tw.start;
    phones.forEach((p, i) => { const d = (span * w[i]) / sum; out.push({ sym: p, t0: t, t1: t + d, word: tw.word }); t += d; });
  });
  return out;
};

// Time-aligned phones + original pitch → VocalFitness articulation of the same utterance.
export const alignedToResponse = (aligned, { duration, f0 }) => {
  const steps = []; let t = 0;
  aligned.forEach((a) => {
    if (a.t0 - t > 0.04) steps.push({ gap: true, dur: (a.t0 - t) * 1000 });
    const st = toSteps([a.sym], { rate: 1 }), tot = st.reduce((s, x) => s + x.dur, 0), span = (a.t1 - a.t0) * 1000;
    st.forEach((s) => steps.push({ ...s, dur: (s.dur * span) / tot }));
    t = a.t1;
  });
  const r = stepsToResponse(steps, { lead: 0, f0, total: duration, transitionMs: 45 });
  r.labels = aligned.map((a) => ({ sym: a.sym, t0: a.t0, t1: a.t1, word: a.word }));
  return r;
};

// Single held posture frame for a phoneme (used by trapezoid / card auto-set).
export const postureFrame = (state) => stateToFrame(state);
